// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Aroya Integration — per-facility connection (Phase 20 GL-1: credentials live in Vault, one
// per facility, write-only after save), room mapping, room discovery, and per-room
// zone/photoperiod configuration (Phase 7 T-706/T-707; zone + photoperiod via RoomConfigDialog).
//
// The browser never sees a saved key. Connect/validate/disconnect go through the aroya-proxy
// edge function; status comes from the integration_health RPC.

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Key, Check, X, AlertCircle, RefreshCw, Search, Settings2, Unplug } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Profile } from "@/types/profile";
import { supabase } from "@/integrations/supabase/client";
import { RoomConfigDialog, type ConfigRoom } from "./RoomConfigDialog";
import {
  defaultFacilityTimezone,
  FACILITY_TIMEZONE_OPTIONS,
  formatPhotoperiodLabel,
} from "@/lib/aroyaPhotoperiod";

interface Zone { id: string; zone_number: number; aroya_zone_id: string | null }
interface Room {
  id: string;
  facility_id: string;
  room_name: string;
  room_type: string;
  aroya_room_id: number | null;
  photoperiod: unknown;
  zones: Zone[];
}
interface Facility { id: string; name: string; timezone: string | null; rooms: Room[] }
interface AroyaRoom { id: number | string; name: string }

interface IntegrationRow {
  provider: string;
  status: "pending" | "active" | "error" | "disabled";
  has_secret: boolean;
  last_validated_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
}
interface RoomRun {
  room_id: string; last_run_at: string; status: string; rows_upserted: number;
  was_empty: boolean; unmapped_zones: string[] | null; error: string | null;
}
interface FacilityHealth { can_manage: boolean; integrations: IntegrationRow[]; rooms: RoomRun[] }

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
const fmtWhen = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : null;

export function AroyaIntegration(_props: { profile: Profile }) {
  const { toast } = useToast();

  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [healthByFacility, setHealthByFacility] = useState<Record<string, FacilityHealth>>({});

  // Per-facility connection drafts (the typed key never persists in state beyond the action).
  const [keyDrafts, setKeyDrafts] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<Record<string, "success" | "error" | null>>({});
  const [busy, setBusy] = useState<Record<string, "test" | "connect" | "disconnect" | null>>({});

  const [roomDrafts, setRoomDrafts] = useState<Record<string, string>>({});
  const [savingRoom, setSavingRoom] = useState<string | null>(null);
  const [isDiscovering, setIsDiscovering] = useState<string | null>(null);
  const [aroyaRoomsByFacility, setAroyaRoomsByFacility] = useState<Record<string, AroyaRoom[]>>({});
  const [configRoom, setConfigRoom] = useState<ConfigRoom | null>(null);
  const [timezoneDrafts, setTimezoneDrafts] = useState<Record<string, string>>({});
  const [savingTimezone, setSavingTimezone] = useState<string | null>(null);

  const loadHealth = useCallback(async (facilityIds: string[]) => {
    const entries = await Promise.all(facilityIds.map(async (fid) => {
      // integration_health is new (GL-1) and not yet in the generated types → cast.
      const { data, error } = await (supabase as any).rpc("integration_health", { p_facility_id: fid });
      if (error) { console.error("integration_health failed", fid, error); return [fid, null] as const; }
      return [fid, data as FacilityHealth] as const;
    }));
    setHealthByFacility((prev) => {
      const next = { ...prev };
      for (const [fid, h] of entries) if (h) next[fid] = h;
      return next;
    });
  }, []);

  const fetchFacilitiesAndRooms = useCallback(async () => {
    setIsLoadingFacilities(true);
    try {
      // Every facility the user is a member of (RLS); non-admins see status only.
      // Cast: select references zones.aroya_zone_id, which predates the generated types.
      const { data, error } = await (supabase as any)
        .from("facilities")
        .select("id, name, timezone, rooms ( id, facility_id, room_name, room_type, aroya_room_id, photoperiod, zones ( id, zone_number, aroya_zone_id ) )")
        .order("name");
      if (error) throw error;

      const facs: Facility[] = (data ?? []).map((f: any) => ({
        id: f.id,
        name: f.name,
        timezone: f.timezone ?? null,
        rooms: (f.rooms ?? [])
          .map((r: any) => ({
            id: r.id,
            facility_id: r.facility_id,
            room_name: r.room_name,
            room_type: r.room_type,
            aroya_room_id: r.aroya_room_id ?? null,
            photoperiod: r.photoperiod ?? null,
            zones: [...(r.zones ?? [])].sort((a: Zone, b: Zone) => a.zone_number - b.zone_number),
          }))
          .sort((a: Room, b: Room) => a.room_name.localeCompare(b.room_name)),
      }));
      setFacilities(facs);
      await loadHealth(facs.map((f) => f.id));
    } catch (error) {
      console.error("Error fetching facilities and rooms:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to load facilities and rooms." });
    } finally {
      setIsLoadingFacilities(false);
    }
  }, [loadHealth, toast]);

  useEffect(() => { fetchFacilitiesAndRooms(); }, [fetchFacilitiesAndRooms]);

  const aroyaRow = (fid: string): IntegrationRow | null =>
    healthByFacility[fid]?.integrations.find((i) => i.provider === "aroya") ?? null;
  const isConnected = (fid: string) => {
    const row = aroyaRow(fid);
    return !!row && row.has_secret && row.status !== "disabled";
  };
  const canManage = (fid: string) => healthByFacility[fid]?.can_manage ?? false;

  const testKey = async (fid: string) => {
    const key = keyDrafts[fid]?.trim();
    if (!key) return;
    setBusy((b) => ({ ...b, [fid]: "test" }));
    setTestResult((t) => ({ ...t, [fid]: null }));
    try {
      const { data, error } = await supabase.functions.invoke("aroya-proxy", {
        body: { action: "validate", facilityId: fid, apiKey: key },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || `Sensor service returned ${data?.status ?? "an error"}`);
      setTestResult((t) => ({ ...t, [fid]: "success" }));
      toast({ title: "Connection Successful", description: "The key is valid. Save it to connect this facility." });
    } catch (error: any) {
      setTestResult((t) => ({ ...t, [fid]: "error" }));
      toast({ variant: "destructive", title: "Connection Failed", description: error.message || "Check the API key." });
    } finally {
      setBusy((b) => ({ ...b, [fid]: null }));
    }
  };

  const connect = async (fid: string) => {
    const key = keyDrafts[fid]?.trim();
    if (!key) return;
    setBusy((b) => ({ ...b, [fid]: "connect" }));
    try {
      const { data, error } = await supabase.functions.invoke("aroya-proxy", {
        body: { action: "connect", facilityId: fid, apiKey: key },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Could not connect");
      setKeyDrafts((d) => { const { [fid]: _, ...rest } = d; return rest; });
      setTestResult((t) => ({ ...t, [fid]: null }));
      await loadHealth([fid]);
      toast({ title: "Connected", description: "The key was validated and stored securely for this facility." });
    } catch (error: any) {
      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to save the API key." });
    } finally {
      setBusy((b) => ({ ...b, [fid]: null }));
    }
  };

  const disconnect = async (fid: string, name: string) => {
    if (!window.confirm(`Disconnect Aroya from ${name}? Syncing stops; historical readings are kept.`)) return;
    setBusy((b) => ({ ...b, [fid]: "disconnect" }));
    try {
      const { data, error } = await supabase.functions.invoke("aroya-proxy", {
        body: { action: "disconnect", facilityId: fid },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Could not disconnect");
      await loadHealth([fid]);
      toast({ title: "Disconnected", description: `${name} no longer syncs sensor data.` });
    } catch (error: any) {
      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to disconnect." });
    } finally {
      setBusy((b) => ({ ...b, [fid]: null }));
    }
  };

  const discoverRooms = async (facility: Facility) => {
    setIsDiscovering(facility.id);
    try {
      const { data, error } = await supabase.functions.invoke("aroya-proxy", {
        body: { action: "rooms", facilityId: facility.id },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Discovery failed");
      const results: AroyaRoom[] = (data.data?.results ?? data.data ?? []) as AroyaRoom[];
      setAroyaRoomsByFacility((m) => ({ ...m, [facility.id]: results }));

      // Suggest matches by normalized name for rooms not yet mapped.
      setRoomDrafts((prev) => {
        const next = { ...prev };
        for (const room of facility.rooms) {
          if (room.aroya_room_id != null || next[room.id]) continue;
          const match = results.find((ar) => norm(ar.name) === norm(room.room_name))
            ?? results.find((ar) => norm(ar.name).includes(norm(room.room_name)));
          if (match) next[room.id] = String(match.id);
        }
        return next;
      });
      toast({ title: "Discovery complete", description: `Found ${results.length} rooms. Suggested matches filled in.` });
    } catch (error: any) {
      toast({ variant: "destructive", title: "Discovery failed", description: error.message || "Could not list rooms." });
    } finally {
      setIsDiscovering(null);
    }
  };

  const saveRoomId = async (roomId: string) => {
    const draft = roomDrafts[roomId];
    if (draft == null || draft === "") return;
    const numeric = parseInt(draft, 10);
    if (isNaN(numeric) || numeric < 0 || numeric > 99999) {
      toast({ variant: "destructive", title: "Invalid Input", description: "Room ID must be 0–99999." });
      return;
    }
    setSavingRoom(roomId);
    try {
      const { error } = await supabase.from("rooms").update({ aroya_room_id: numeric }).eq("id", roomId);
      if (error) throw error;
      setFacilities((facs) => facs.map((f) => ({
        ...f, rooms: f.rooms.map((r) => (r.id === roomId ? { ...r, aroya_room_id: numeric } : r)),
      })));
      setRoomDrafts((prev) => { const { [roomId]: _, ...rest } = prev; return rest; });
      toast({ title: "Room mapped", description: `Mapped to Room ID ${numeric}.` });
    } catch (error: any) {
      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to save Room ID." });
    } finally {
      setSavingRoom(null);
    }
  };

  const saveTimezone = async (facilityId: string) => {
    const tz = timezoneDrafts[facilityId];
    if (!tz) return;
    setSavingTimezone(facilityId);
    try {
      const { error } = await (supabase as any)
        .from("facilities").update({ timezone: tz }).eq("id", facilityId);
      if (error) throw error;
      setFacilities((facs) => facs.map((f) => (f.id === facilityId ? { ...f, timezone: tz } : f)));
      setTimezoneDrafts((prev) => { const { [facilityId]: _, ...rest } = prev; return rest; });
      toast({ title: "Timezone saved", description: "Aura day/night splits will use this timezone." });
    } catch (error: any) {
      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to save timezone." });
    } finally {
      setSavingTimezone(null);
    }
  };

  const mappedZoneCount = (r: Room) => r.zones.filter((z) => z.aroya_zone_id).length;

  const renderConnection = (facility: Facility) => {
    const fid = facility.id;
    const row = aroyaRow(fid);
    const connected = isConnected(fid);
    const manage = canManage(fid);
    const draft = keyDrafts[fid] ?? "";
    const state = busy[fid];
    const result = testResult[fid];

    const statusBadge = !row || row.status === "disabled" || !row.has_secret
      ? <Badge variant="secondary">Not connected</Badge>
      : row.status === "error"
        ? <Badge variant="destructive">Error</Badge>
        : <Badge className="bg-green-600 hover:bg-green-600">Connected</Badge>;

    return (
      <div className="rounded-md border p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Aroya account</span>
            {statusBadge}
          </div>
          <div className="text-xs text-muted-foreground">
            {connected && row?.last_validated_at ? `Key saved · validated ${fmtWhen(row.last_validated_at)}` : null}
            {connected && row?.last_ok_at ? ` · last sync ${fmtWhen(row.last_ok_at)}` : null}
          </div>
        </div>
        {row?.status === "error" && row.last_error && (
          <Alert variant="destructive">
            <X className="h-4 w-4" />
            <AlertDescription>{row.last_error}</AlertDescription>
          </Alert>
        )}
        {manage ? (
          <>
            <div className="space-y-2">
              <Label htmlFor={`api-key-${fid}`}>{connected ? "Replace API key" : "API key"}</Label>
              <div className="flex gap-2">
                <Input id={`api-key-${fid}`} type="password" autoComplete="off"
                  placeholder={connected ? "Enter a new key to rotate" : "Enter your Aroya API key"}
                  value={draft} onChange={(e) => setKeyDrafts((d) => ({ ...d, [fid]: e.target.value }))}
                  className="flex-1" />
                <Button variant="outline" onClick={() => testKey(fid)} disabled={!draft.trim() || !!state}>
                  {state === "test" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                  Test
                </Button>
                <Button onClick={() => connect(fid)} disabled={!draft.trim() || !!state}>
                  {state === "connect" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {connected ? "Rotate key" : "Connect"}
                </Button>
                {connected && (
                  <Button variant="outline" onClick={() => disconnect(fid, facility.name)} disabled={!!state} title="Disconnect">
                    {state === "disconnect" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />}
                  </Button>
                )}
              </div>
            </div>
            {result === "success" && (
              <Alert className="bg-green-50 border-green-200">
                <Check className="h-4 w-4 text-primary" />
                <AlertDescription className="text-primary">The key is valid. Click Connect to store it.</AlertDescription>
              </Alert>
            )}
            {result === "error" && (
              <Alert variant="destructive">
                <X className="h-4 w-4" />
                <AlertDescription>Connection failed. Check the API key and try again.</AlertDescription>
              </Alert>
            )}
            <div className="text-xs text-muted-foreground">
              <AlertCircle className="h-3.5 w-3.5 inline-block mr-1" />
              Keys are stored encrypted for this facility only and are never shown again after saving. Enter a new key to rotate.
            </div>
          </>
        ) : (
          <div className="text-xs text-muted-foreground">
            Only facility owners and admins can connect or rotate this key.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Sensor Connection &amp; Room Mapping</CardTitle>
          <CardDescription>
            Connect your Aroya account per facility, then map your rooms to sensor room IDs and configure zones + photoperiod.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingFacilities && facilities.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">Loading facilities…</span>
            </div>
          ) : facilities.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No facilities found. Please create a facility first.</p>
              <Button variant="outline" onClick={() => (window.location.href = "/profile/facilities")}>Manage Facilities</Button>
            </div>
          ) : (
            <div className="space-y-10">
              {facilities.map((facility) => {
                const tzDraft = timezoneDrafts[facility.id];
                const currentTz = facility.timezone || defaultFacilityTimezone();
                const tzValue = tzDraft ?? currentTz;
                const tzDirty = tzDraft != null && tzDraft !== (facility.timezone || "");
                const connected = isConnected(facility.id);
                const runs = healthByFacility[facility.id]?.rooms ?? [];
                const runByRoom = Object.fromEntries(runs.map((r) => [r.room_id, r]));
                const aroyaRooms = aroyaRoomsByFacility[facility.id];
                return (
                <div key={facility.id} className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold">{facility.name}</h3>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">Facility timezone</Label>
                      <Select
                        value={tzValue}
                        onValueChange={(v) => setTimezoneDrafts((p) => ({ ...p, [facility.id]: v }))}
                      >
                        <SelectTrigger className="h-8 w-[220px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FACILITY_TIMEZONE_OPTIONS.map((tz) => (
                            <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                          ))}
                          {!FACILITY_TIMEZONE_OPTIONS.includes(tzValue as typeof FACILITY_TIMEZONE_OPTIONS[number]) && (
                            <SelectItem value={tzValue}>{tzValue}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant={tzDirty ? "default" : "outline"}
                        disabled={!tzDirty || savingTimezone === facility.id}
                        onClick={() => saveTimezone(facility.id)}>
                        {savingTimezone === facility.id
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : "Save TZ"}
                      </Button>
                    </div>
                  </div>

                  {renderConnection(facility)}

                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">Rooms</div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => discoverRooms(facility)}
                        disabled={isDiscovering === facility.id || !connected}
                        title={connected ? "List rooms from your Aroya account" : "Connect this facility first"}>
                        {isDiscovering === facility.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                        Discover
                      </Button>
                      <Button variant="outline" size="sm" onClick={fetchFacilitiesAndRooms} disabled={isLoadingFacilities}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingFacilities ? "animate-spin" : ""}`} />Refresh
                      </Button>
                    </div>
                  </div>

                  {facility.rooms.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No rooms found for this facility.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Room</TableHead>
                          <TableHead className="w-[180px]">Room ID</TableHead>
                          <TableHead className="w-[110px]">Zones</TableHead>
                          <TableHead className="w-[120px]">Photoperiod</TableHead>
                          <TableHead className="w-[150px]">Last synced</TableHead>
                          <TableHead className="w-[180px] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {facility.rooms.map((room) => {
                          const hasAroyaId = room.aroya_room_id !== null;
                          const draft = roomDrafts[room.id];
                          const isPending = draft != null && draft !== "" && draft !== String(room.aroya_room_id ?? "");
                          const suggested = aroyaRooms && isPending && room.aroya_room_id == null;
                          const sync = runByRoom[room.id];
                          const mapped = mappedZoneCount(room);
                          return (
                            <TableRow key={room.id}>
                              <TableCell>
                                <div className="font-medium">{room.room_name}</div>
                                <div className="text-xs text-muted-foreground">{room.room_type}</div>
                              </TableCell>
                              <TableCell>
                                <Input type="number" placeholder="Room ID" min={0} max={99999}
                                  value={draft ?? room.aroya_room_id ?? ""}
                                  onChange={(e) => setRoomDrafts((p) => ({ ...p, [room.id]: e.target.value }))}
                                  className={hasAroyaId && !isPending ? "border-green-500" : suggested ? "border-amber-400" : ""} />
                                {suggested && <span className="text-[11px] text-amber-600">suggested</span>}
                              </TableCell>
                              <TableCell>
                                {room.zones.length === 0
                                  ? <span className="text-xs text-muted-foreground">—</span>
                                  : <Badge variant={mapped === room.zones.length ? "default" : "secondary"}>
                                      {mapped}/{room.zones.length}
                                    </Badge>}
                              </TableCell>
                              <TableCell>
                                <span className="text-xs tabular-nums text-muted-foreground">
                                  {formatPhotoperiodLabel(room.photoperiod)}
                                </span>
                              </TableCell>
                              <TableCell>
                                {sync ? (
                                  <div className="text-xs">
                                    <div className={sync.status === "error" ? "text-destructive" : "text-muted-foreground"}>
                                      {fmtWhen(sync.last_run_at)}
                                    </div>
                                    <div className="text-muted-foreground">
                                      {sync.status === "error" ? (sync.error ?? "error") : sync.was_empty ? "no data" : `${sync.rows_upserted} rows`}
                                      {sync.unmapped_zones?.length ? ` · ${sync.unmapped_zones.length} unmapped` : ""}
                                    </div>
                                  </div>
                                ) : <span className="text-xs text-muted-foreground">never</span>}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button size="sm" variant={isPending ? "default" : "outline"}
                                    onClick={() => saveRoomId(room.id)} disabled={!isPending || savingRoom === room.id}>
                                    {savingRoom === room.id ? <Loader2 className="h-4 w-4 animate-spin" /> : hasAroyaId ? "Update" : "Save"}
                                  </Button>
                                  <Button size="sm" variant="outline" disabled={!hasAroyaId || !connected}
                                    onClick={() => setConfigRoom(room)} title="Map zones + photoperiod">
                                    <Settings2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              );})}
            </div>
          )}
        </CardContent>
      </Card>

      <RoomConfigDialog
        room={configRoom}
        open={configRoom !== null}
        onOpenChange={(v) => { if (!v) setConfigRoom(null); }}
        onSaved={fetchFacilitiesAndRooms}
      />
    </div>
  );
}
